import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * Datenschutzerklärung gemäß DSGVO (Art. 13, 14)
 * Stand: Februar 2026 — Daten der DuTACS GmbH.
 */
export default function PrivacyPage() {
    return (
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950 py-12" data-testid="privacy-page">
            <div className="max-w-3xl mx-auto px-6">
                <Link to="/login" className="inline-flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200 mb-6" data-testid="privacy-back-link">
                    <ArrowLeft className="w-4 h-4" /> Zurück
                </Link>

                <h1 className="text-4xl font-bold tracking-tight mb-2">Datenschutzerklärung</h1>
                <p className="text-sm text-neutral-500 mb-8">Stand: Februar 2026</p>

                <section className="prose dark:prose-invert max-w-none space-y-6 text-sm leading-relaxed">

                    <div>
                        <h2 className="text-xl font-semibold mb-2">1. Verantwortlicher</h2>
                        <p>
                            Verantwortlicher im Sinne der Datenschutz-Grundverordnung (DSGVO) sowie sonstiger
                            nationaler Datenschutzgesetze und datenschutzrechtlicher Bestimmungen:
                        </p>
                        <p className="mt-2">
                            <strong>DuTACS GmbH</strong><br />
                            Mörfelder Straße 97<br />
                            65451 Kelsterbach, Deutschland<br />
                            Telefon: +49 (0)6107 9899963-0<br />
                            E-Mail: <a href="mailto:info@dutacs.de" className="underline">info@dutacs.de</a>
                        </p>
                        <p className="mt-2 text-xs text-neutral-500">
                            Geschäftsführer: Rüstü Dücan, Veysel Pountso, Seyfullah Pountso · Amtsgericht Darmstadt HRB 104140 · USt-ID DE357990281
                        </p>
                    </div>

                    <div>
                        <h2 className="text-xl font-semibold mb-2">2. Datenschutz-Anfragen</h2>
                        <p>
                            Anfragen zu Datenschutz, Auskunft, Berichtigung oder Löschung Ihrer personenbezogenen
                            Daten richten Sie bitte direkt an:
                            {" "}
                            <a href="mailto:datenschutz@dutacs.de" className="underline">datenschutz@dutacs.de</a>
                        </p>
                        <p className="mt-2 text-xs text-neutral-500">
                            Ein Datenschutzbeauftragter ist nach Art. 37 DSGVO bzw. § 38 BDSG gegenwärtig nicht
                            zwingend zu benennen. Datenschutzanfragen werden direkt durch die Geschäftsführung
                            bearbeitet.
                        </p>
                    </div>

                    <div>
                        <h2 className="text-xl font-semibold mb-2">3. Allgemeines zur Datenverarbeitung</h2>
                        <p>
                            Wir verarbeiten personenbezogene Daten unserer Nutzer grundsätzlich nur, soweit dies
                            zur Bereitstellung einer funktionsfähigen Anwendung sowie unserer Inhalte und Leistungen
                            erforderlich ist. Rechtsgrundlagen sind in der Regel:
                        </p>
                        <ul className="list-disc pl-6 space-y-1 mt-2">
                            <li>Art. 6 Abs. 1 lit. a DSGVO — Einwilligung</li>
                            <li>Art. 6 Abs. 1 lit. b DSGVO — Vertragserfüllung (Nutzung von MyTACS als unternehmensinterne Anwendung)</li>
                            <li>Art. 6 Abs. 1 lit. c DSGVO — rechtliche Verpflichtung (z. B. handels- und steuerrechtliche Aufbewahrungspflichten)</li>
                            <li>Art. 6 Abs. 1 lit. f DSGVO — berechtigtes Interesse (IT-Sicherheit, Missbrauchsprävention)</li>
                        </ul>
                    </div>

                    <div>
                        <h2 className="text-xl font-semibold mb-2">4. Welche Daten wir verarbeiten</h2>
                        <ul className="list-disc pl-6 space-y-1">
                            <li><strong>Account-Daten:</strong> Name, E-Mail, Telefonnummer, Geburtstag, Personalnummer, Jobtitel, Abteilung.</li>
                            <li><strong>Authentifizierung:</strong> Verschlüsseltes Passwort (bcrypt-Hash), 2FA-Geheimnis (verschlüsselt gespeichert), Backup-Codes.</li>
                            <li><strong>Zugriffsdaten:</strong> IP-Adresse, User-Agent, Zeitstempel der Logins (Audit-Log gemäß DSGVO-Rechenschaftspflicht, Art. 5 Abs. 2).</li>
                            <li><strong>Inhaltsdaten:</strong> Aufgaben, Notizen, Kalendereinträge, Zeiterfassung, Chat-Nachrichten, Datei-Anhänge.</li>
                            <li><strong>Mailbox-Integration (optional):</strong> Microsoft 365 OAuth-Tokens (verschlüsselt), Mail-Metadaten zur KI-Klassifizierung.</li>
                            <li><strong>Messenger-Integration (optional):</strong> WhatsApp- und Superchat-Nachrichten-Inhalte zur Aufgabenerstellung und -zuweisung.</li>
                        </ul>
                    </div>

                    <div>
                        <h2 className="text-xl font-semibold mb-2">5. Wo werden die Daten gespeichert?</h2>
                        <p>
                            Die Verarbeitung erfolgt ausschließlich auf Servern und in Rechenzentren in der
                            Bundesrepublik Deutschland:
                        </p>
                        <ul className="list-disc pl-6 space-y-1">
                            <li><strong>Anwendung & Datenbank:</strong> Hetzner Online GmbH, Rechenzentrum Falkenstein, Deutschland (Cloud-Server).</li>
                            <li><strong>Objektspeicher (Datei-Anhänge):</strong> Hetzner Object Storage, Standort Falkenstein, Deutschland.</li>
                            <li><strong>Verschlüsselte Backups:</strong> Hetzner Object Storage, Falkenstein (AES-256-GCM-verschlüsselt).</li>
                            <li><strong>E-Mail-Integration:</strong> Microsoft 365 (Microsoft Ireland Operations Ltd., EU-Rechenzentren).</li>
                            <li><strong>KI-Verarbeitung (Mail-Klassifizierung, Notizen):</strong> Anthropic Claude API. Inhalte werden ausschließlich für die einmalige Klassifizierung übermittelt und gemäß den Anthropic-Datenschutzrichtlinien NICHT zum Modelltraining verwendet (Zero-Retention-Vereinbarung).</li>
                        </ul>
                    </div>

                    <div>
                        <h2 className="text-xl font-semibold mb-2">6. Ihre Rechte gemäß DSGVO</h2>
                        <p>Sie haben jederzeit folgende Rechte:</p>
                        <ul className="list-disc pl-6 space-y-1">
                            <li><strong>Auskunft</strong> (Art. 15 DSGVO): welche Daten wir über Sie gespeichert haben.</li>
                            <li><strong>Berichtigung</strong> (Art. 16 DSGVO): Korrektur falscher oder unvollständiger Daten.</li>
                            <li><strong>Löschung / &bdquo;Recht auf Vergessenwerden&ldquo;</strong> (Art. 17 DSGVO): über Einstellungen → Konto löschen (Self-Service) oder per E-Mail an datenschutz@dutacs.de.</li>
                            <li><strong>Einschränkung der Verarbeitung</strong> (Art. 18 DSGVO).</li>
                            <li><strong>Datenübertragbarkeit</strong> (Art. 20 DSGVO): JSON-Export aller Daten unter Einstellungen → Daten exportieren.</li>
                            <li><strong>Widerspruch</strong> (Art. 21 DSGVO).</li>
                            <li><strong>Beschwerde bei der Aufsichtsbehörde</strong> (Art. 77 DSGVO): zuständig ist der Hessische Beauftragte für Datenschutz und Informationsfreiheit (HBDI), Postfach 3163, 65021 Wiesbaden.</li>
                        </ul>
                        <p className="mt-3">
                            Anfragen bitte an: <a href="mailto:datenschutz@dutacs.de" className="underline">datenschutz@dutacs.de</a>
                        </p>
                    </div>

                    <div>
                        <h2 className="text-xl font-semibold mb-2">7. Cookies & lokaler Speicher</h2>
                        <p>
                            Wir verwenden ausschließlich <strong>technisch notwendige Cookies</strong> sowie
                            den Local Storage des Browsers, um Sie eingeloggt zu halten und Ihre UI-Einstellungen
                            (z. B. Sidebar-Status, Sprache) zu speichern. Tracking-Cookies, Marketing-Cookies oder
                            Analyse-Cookies werden <strong>nicht</strong> eingesetzt. Eine Einwilligung ist daher
                            nicht erforderlich (§ 25 Abs. 2 Nr. 2 TTDSG).
                        </p>
                    </div>

                    <div>
                        <h2 className="text-xl font-semibold mb-2">8. Aufbewahrungsfristen</h2>
                        <p>
                            Personenbezogene Daten werden gelöscht, sobald der Zweck der Verarbeitung entfällt.
                            Daten, die einer gesetzlichen Aufbewahrungspflicht unterliegen (z. B. Rechnungen und
                            geschäftliche Korrespondenz nach § 147 AO und § 257 HGB → 6 bzw. 10 Jahre), werden
                            für die jeweilige Frist aufbewahrt und anschließend sicher gelöscht.
                        </p>
                    </div>

                    <div>
                        <h2 className="text-xl font-semibold mb-2">9. Datensicherheit</h2>
                        <ul className="list-disc pl-6 space-y-1">
                            <li>Transportverschlüsselung: TLS 1.3 (HTTPS überall, automatische Erneuerung über Let&apos;s Encrypt).</li>
                            <li>Passwort-Speicherung: bcrypt-Hash (kein Klartext).</li>
                            <li>2-Faktor-Authentifizierung: TOTP (Microsoft / Google Authenticator), für administrative Konten verpflichtend.</li>
                            <li>Verschlüsselte Datenbank-Backups (AES-256-GCM).</li>
                            <li>Brute-Force-Schutz: maximal 5 fehlerhafte Login-Versuche pro 15 Minuten.</li>
                            <li>Audit-Log aller sicherheitsrelevanten Aktionen (Anmeldungen, Datenexporte, Löschungen).</li>
                            <li>Firewall mit ausschließlich freigegebenen Ports (22/SSH, 80/443/HTTPS).</li>
                        </ul>
                    </div>

                    <div>
                        <h2 className="text-xl font-semibold mb-2">10. Auftragsverarbeiter</h2>
                        <p>
                            Folgende Dienstleister verarbeiten personenbezogene Daten in unserem Auftrag.
                            Mit allen Anbietern wurden bzw. werden Auftragsverarbeitungsverträge gemäß Art. 28
                            DSGVO geschlossen:
                        </p>
                        <ul className="list-disc pl-6 space-y-1">
                            <li><strong>Hetzner Online GmbH</strong>, Industriestraße 25, 91710 Gunzenhausen — Hosting (Cloud-Server, Object Storage, Backups). Rechenzentrum Falkenstein, Deutschland.</li>
                            <li><strong>Microsoft Ireland Operations Ltd.</strong>, One Microsoft Place, South County Business Park, Leopardstown, Dublin 18, Irland — E-Mail-Integration (Microsoft 365 / Graph API), EU-Rechenzentren.</li>
                            <li><strong>Anthropic PBC</strong>, San Francisco, USA — KI-Verarbeitung (Mail-Klassifizierung). Datenübermittlung in die USA auf Grundlage der EU-Standardvertragsklauseln (SCC) gemäß Art. 46 Abs. 2 lit. c DSGVO.</li>
                        </ul>
                    </div>

                    <div>
                        <h2 className="text-xl font-semibold mb-2">11. Änderungen dieser Datenschutzerklärung</h2>
                        <p>
                            Wir behalten uns vor, diese Datenschutzerklärung anzupassen, damit sie stets den
                            aktuellen rechtlichen Anforderungen entspricht oder um Änderungen unserer Leistungen
                            umzusetzen, z. B. bei der Einführung neuer Services. Es gilt jeweils die zum Zeitpunkt
                            Ihres Zugriffs abrufbare Fassung.
                        </p>
                    </div>
                </section>
            </div>
        </div>
    );
}
